import { Divider, Menu } from 'antd'
import scrollTo from 'antd/lib/_util/scrollTo'
import { useRouter } from 'next/router'
import { FunctionComponent, useEffect, useState } from 'react'
import { useMount } from 'react-use'
import styled from 'styled-components'
import { FeatureFlag, useFetchUserStatusQuery } from '@/api/user-api'
import AvatarInfo from '@/components/common/avatar-info'
import { Icon } from '@/components/custom-icon'
import { useAuth } from '@/hooks/use-auth'
import useCampaignVisible from '@/hooks/use-campaign-visible'
import useFeatureFlags from '@/hooks/use-feature-flags'
import { useIntl } from '@/i18n/hooks/use-intl'
import { ampli, PromiseResult, Result } from '@/utils/ampli'
import { goToPage } from '@/utils/routes/go-to-page'
import { getWorkspaceTypeI18nId } from '@/utils/workspace/get-workspace-type-i18n-id'

const { Item } = Menu

const getTabKey = (pathname: string): string => {
  return pathname.split('/')[1]
}

const IrmSettingSiderContent: FunctionComponent<{ isCollapsed: boolean }> = ({
  isCollapsed,
}) => {
  const { formatMessage } = useIntl()
  const router = useRouter()

  const { userInfo } = useAuth()
  const campaignVisible = useCampaignVisible()
  const { withFeature } = useFeatureFlags()
  const { data: userStatus } = useFetchUserStatusQuery()

  const currentWorkspace = userStatus?.currentWorkspace

  const [selectedPage, setSelectedPage] = useState('')

  useEffect(() => {
    setSelectedPage(getTabKey(router.pathname))
  }, [router.pathname])

  const handleClick = async (e): Promise<void> => {
    await goToPage(`/${e.key}`)
  }

  useMount((): void => {
    scrollTo(0)
  })

  return (
    <MenuWrapper>
      <AvatarInfo
        description={userInfo?.email}
        isCollapsed={isCollapsed}
        name={userInfo?.username}
      />
      <Menu mode='inline' selectedKeys={[selectedPage]} onClick={handleClick}>
        <Item key='settings'>
          <Icon type='user' />
          <span>{formatMessage({ id: 'account:side_personal_info' })}</span>
        </Item>
        <Item key='settings-password'>
          <Icon type='key' />
          <span>{formatMessage({ id: 'account:side_change_psw' })}</span>
        </Item>
      </Menu>

      <>
        <Divider />
        <AvatarInfo
          description={`${formatMessage({
            id: getWorkspaceTypeI18nId(
              currentWorkspace?.workspaceType ?? undefined,
            ),
          })} - ${formatMessage(
            { id: 'general:navbar_workspace_member_count' },
            { n: currentWorkspace?.memberAmount },
          )}`}
          imgSrc={currentWorkspace?.iconUrl ?? undefined}
          isCollapsed={isCollapsed}
          name={currentWorkspace?.name}
        />
        <Menu mode='inline' selectedKeys={[selectedPage]} onClick={handleClick}>
          <Item key='workspace-setting'>
            <Icon type='elIconSetting' />
            <span>
              {formatMessage({ id: 'workspace:setting_sidebar_title' })}
            </span>
          </Item>
          {campaignVisible && withFeature(FeatureFlag.DualWheel) && (
            <Item key='integrations'>
              <Icon type='appstore' />
              <span>
                {formatMessage({
                  id: 'workspace:setting_sidebar_integration',
                })}
              </span>
            </Item>
          )}
          <Item key='workspace-member'>
            <Icon type='elIconUsers' />
            <span>
              {formatMessage({ id: 'workspace:setting_sidebar_members' })}
            </span>
          </Item>
          <Item
            key='workspace-plan'
            onClick={(): PromiseResult<Result> =>
              ampli.clickWorkspacePlan({
                path: router.asPath,
                clickPlanSource: 'Sider',
              })
            }
          >
            <Icon type='elIconCrown' />
            <span>
              {formatMessage({ id: 'workspace:setting_sidebar_plans' })}
            </span>
          </Item>
          <Item key='workspace-history'>
            <Icon type='dollar' />
            <span>
              {formatMessage({
                id: 'workspace:setting_sidebar_order_history',
              })}
            </span>
          </Item>
          <Item key='workspace-valuation'>
            <Icon type='calculator' />
            <span>
              {formatMessage({ id: 'workspace:setting_sidebar_womm' })}
            </span>
          </Item>
        </Menu>
      </>
    </MenuWrapper>
  )
}

const MenuWrapper = styled.div`
  .ant-menu {
    border-right: none;

    &-sub {
      background: transparent !important;
    }
  }
`
export default IrmSettingSiderContent
